import * as ImagePicker from "expo-image-picker";
import { useCallback, useEffect, useState } from "react";
import { FlatList, ListRenderItem } from "react-native";

import { Banner, CalloutRefresh, PhotoListItem } from "../components";
import { Button, Col } from "../lib";
import {
  Attention,
  Image,
  useUser,
  useUserImageDelete,
  useUserImagesOrderUpdate,
  useUserImageStore,
} from "../services";
import { LoadingScreen } from "./loading";

export function PhotosScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
    invalidate: invalidateUser,
  } = useUser();
  const [storeUserImage, isUplopading] = useUserImageStore();
  const [deleteUserImage, isDeleting] = useUserImageDelete();
  const [updateUserImagesOrder, isUpdatingOrder] = useUserImagesOrderUpdate();

  // State
  const [images, setImages] = useState<Image[]>([]);

  // Vars
  const isLoading = isUplopading || isDeleting || isUpdatingOrder;

  // Handlers
  const handlePressLibrary = useCallback(async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      allowsMultipleSelection: true,
    });

    if (!result.canceled) {
      await Promise.all(result.assets.map(storeUserImage));
    }

    invalidateUser();
  }, [invalidateUser, storeUserImage]);

  const handleMoveUpItem = useCallback(
    async (id: number) => {
      const oldIndex = images.findIndex((image) => image.id === id);
      if (oldIndex <= 0) return; // Already at the top

      // Create a copy of the array
      const newImages = [...images];
      // Remove the item
      const [moved] = newImages.splice(oldIndex, 1);
      // Insert at oldIndex - 1
      newImages.splice(oldIndex - 1, 0, moved);

      await updateUserImagesOrder({
        order: newImages.map((image, idx) => ({ id: image.id, position: idx })),
      });

      invalidateUser();
    },
    [images, invalidateUser, updateUserImagesOrder]
  );

  const handleMoveDownItem = useCallback(
    async (id: number) => {
      const oldIndex = images.findIndex((image) => image.id === id);
      if (oldIndex >= images.length - 1) return; // Already at the bottom

      // Create a copy of the array
      const newImages = [...images];
      // Remove the item
      const [moved] = newImages.splice(oldIndex, 1);
      // Insert at oldIndex + 1
      newImages.splice(oldIndex + 1, 0, moved);

      await updateUserImagesOrder({
        order: newImages.map((image, idx) => ({ id: image.id, position: idx })),
      });

      invalidateUser();
    },
    [images, invalidateUser, updateUserImagesOrder]
  );

  const handleDeleteItem = useCallback(
    async (id: number) => {
      const ok = await Attention.confirm(
        "Delete Photo",
        "Are you sure you want to delete this Photo?"
      );

      if (ok) {
        await deleteUserImage({ id });

        invalidateUser();
      }
    },
    [deleteUserImage, invalidateUser]
  );

  const renderItem = useCallback(
    (image: Image, index: number) => (
      <PhotoListItem
        {...image}
        disabled={isLoading}
        first={index === 0}
        last={index === images.length - 1}
        onDelete={handleDeleteItem}
        onMoveDown={handleMoveDownItem}
        onMoveUp={handleMoveUpItem}
      />
    ),
    [
      handleDeleteItem,
      handleMoveDownItem,
      handleMoveUpItem,
      images.length,
      isLoading,
    ]
  );

  const handleRenderItem: ListRenderItem<Image> = useCallback(
    ({ item, index }) => renderItem(item, index),
    [renderItem]
  );

  // Effects
  useEffect(() => {
    setImages(user?.images ?? []);
  }, [user]);

  // Render
  if (isUserLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <CalloutRefresh onRefresh={refetchUser} />;
  }

  const footer = (
    <Col m="4">
      <Button
        icon="plus"
        loading={isLoading}
        onPress={handlePressLibrary}
        disabled={!user.independent && !user.super}
      >
        Upload new photos
      </Button>
    </Col>
  );

  const header = (
    <Banner message="Your photos cannot include alcohol or sex. Nor should they include any other person than you." />
  );

  return (
    <FlatList
      data={images}
      keyExtractor={({ id }) => `photo-${id}`}
      renderItem={handleRenderItem}
      ListHeaderComponent={header}
      ListFooterComponent={footer}
    />
  );
}
